// A4 printable product list (name / SKU / warehouse location, sorted by location).
// Pure functions so the sort order and HTML output are unit-testable.

import { escapeHtml } from './escapeHtml';

const UNSET_LOCATION_LABELS = new Set(['', 'not set', 'n/a', 'none']);

const normalizeLocation = (value) => {
  const text = String(value ?? '').trim();
  if (UNSET_LOCATION_LABELS.has(text.toLowerCase())) return '';
  return text.toUpperCase();
};

// Natural sort (P1-P2 before P1-P10); products without a location sink to the bottom.
export const compareBinLocations = (a, b) => {
  const locA = normalizeLocation(a);
  const locB = normalizeLocation(b);
  if (!locA && !locB) return 0;
  if (!locA) return 1;
  if (!locB) return -1;
  return locA.localeCompare(locB, undefined, { numeric: true, sensitivity: 'base' });
};

export const sortItemsByLocation = (items) => [...items].sort((a, b) => (
  compareBinLocations(a.location, b.location)
  || String(a.sku ?? '').localeCompare(String(b.sku ?? ''), undefined, { numeric: true, sensitivity: 'base' })
));

// Print windows open on the staff origin, so the page gets the same lockdown as
// the job ticket: every value escaped, a CSP meta that blocks script, and no
// inline script of its own. The opener calls print() after document.close().
export const PRINT_CSP_META = '<meta http-equiv="Content-Security-Policy" content="default-src \'none\'; style-src \'unsafe-inline\'; img-src data: https:">';

/**
 * Build a standalone A4 print page for the given items.
 * @param {Array<{title: string, variantTitle?: string, sku: string, location: string, stock?: number|null, stockCommitted?: number}>} items
 * @param {{generatedAt?: Date, heading?: string, printedBy?: string, note?: string}} [options]
 * @returns {string} full HTML document
 */
export const buildA4ProductListHtml = (items, { generatedAt = new Date(), heading = 'Warehouse Product List', printedBy = '', note = '' } = {}) => {
  const sorted = sortItemsByLocation(items);

  const rows = sorted.map((item) => {
    const location = normalizeLocation(item.location);
    const variant = String(item.variantTitle ?? '').trim();
    // Stock unknown (old callers) prints as a dash, not a misleading 0.
    const stock = item.stock ?? null;
    const committed = Number(item.stockCommitted) > 0
      ? `<span class="committed"> (A:${escapeHtml(item.stockCommitted)})</span>`
      : '';
    return `      <tr>
        <td class="loc">${location ? escapeHtml(location) : '<span class="unset">Not set</span>'}</td>
        <td class="sku">${escapeHtml(item.sku)}</td>
        <td>${escapeHtml(item.title)}${variant ? `<span class="variant"> — ${escapeHtml(variant)}</span>` : ''}</td>
        <td class="stock">${stock === null ? '—' : escapeHtml(stock)}${committed}</td>
      </tr>`;
  }).join('\n');

  const printedAt = generatedAt.toLocaleString('en-AU', {
    day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit'
  });

  const printedByText = String(printedBy ?? '').trim();
  const noteText = String(note ?? '').trim();

  return `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="utf-8">
${PRINT_CSP_META}
<title>${escapeHtml(heading)}</title>
<style>
  @page { size: A4 portrait; margin: 12mm; }
  * { box-sizing: border-box; }
  body { font-family: -apple-system, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif; color: #111; margin: 0; padding: 16px; font-size: 12px; }
  header { display: flex; justify-content: space-between; align-items: baseline; border-bottom: 2px solid #111; padding-bottom: 6px; margin-bottom: 10px; }
  h1 { font-size: 16px; margin: 0; }
  .meta { font-size: 11px; color: #555; }
  table { width: 100%; border-collapse: collapse; }
  thead { display: table-header-group; }
  th { text-align: left; font-size: 10px; text-transform: uppercase; letter-spacing: 0.05em; color: #555; border-bottom: 1px solid #999; padding: 4px 8px 4px 0; }
  td { border-bottom: 1px solid #ddd; padding: 5px 8px 5px 0; vertical-align: top; }
  tr { page-break-inside: avoid; }
  .loc { font-family: 'Consolas', 'SF Mono', monospace; font-weight: 700; white-space: nowrap; width: 1%; }
  .sku { font-family: 'Consolas', 'SF Mono', monospace; white-space: nowrap; width: 1%; }
  .stock { font-family: 'Consolas', 'SF Mono', monospace; white-space: nowrap; width: 1%; text-align: right; }
  .committed { color: #b00; }
  .variant { color: #555; }
  .unset { color: #b00; font-weight: 400; font-style: italic; }
  .printed-by { color: #111; font-weight: 700; }
  .note { border: 1.5px solid #111; border-radius: 6px; padding: 8px 10px; margin-bottom: 10px; font-size: 13px; font-weight: 600; background: #fffbe6; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  @media print { body { padding: 0; } }
</style>
</head>
<body>
  <header>
    <h1>${escapeHtml(heading)}</h1>
    <div class="meta">${sorted.length} item${sorted.length === 1 ? '' : 's'} · ${escapeHtml(printedAt)}${printedByText ? ` · <span class="printed-by">Printed by ${escapeHtml(printedByText)}</span>` : ''}</div>
  </header>
${noteText ? `  <div class="note">📝 ${escapeHtml(noteText)}</div>\n` : ''}  <table>
    <thead>
      <tr><th>Location</th><th>SKU</th><th>Product</th><th class="stock">Stock</th></tr>
    </thead>
    <tbody>
${rows}
    </tbody>
  </table>
</body>
</html>`;
};
